import { MapPin, Phone, Wrench } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { Highlight } from '@/components/Highlight';
import { TruckIcon } from '@/components/icons/TruckIcon';
import { LeadDueDateBadge } from '@/components/LeadDueDateBadge';
import { TemporalFormatter } from '@/components/TemporalFormatter';
import type { Lead } from '@/models/entities/lead';

export interface LeadCardProps {
  readonly lead: Lead;
  readonly searchTerm?: string;
}

export function LeadCard({ lead, searchTerm }: LeadCardProps) {
  const location = useLocation();
  const maintenanceDescription = lead.maintenance.maintenanceItems
    .map((item) => item.description)
    .join(', ');

  return (
    <li
      id={lead.id}
      className="bg-white border rounded p-3 transition-all relative border-gray-300 hover:border-gray-400 has-[a.active]:border-orange-500 has-[a.active]:shadow-md"
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-bold text-sm">
          <NavLink
            to={{ pathname: `/leads/${lead.id}`, search: location.search }}
            className="focus:outline-none after:absolute after:inset-0 after:rounded"
          >
            <Highlight text={lead.customer.name} term={searchTerm} />
          </NavLink>
        </h4>
        <LeadDueDateBadge lead={lead} />
      </div>

      <dl className="space-y-1.5 text-gray-700">
        <div className="flex items-center gap-2 text-xs">
          <Wrench className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <dt className="sr-only">Maintenance</dt>
          <dd className="truncate">
            <Highlight text={maintenanceDescription} term={searchTerm} />
          </dd>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <TruckIcon className="w-3.5 h-3.5 shrink-0" />
          <dt className="sr-only">VIN</dt>
          <dd className="truncate">
            <Highlight text={lead.vin} term={searchTerm} />
          </dd>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Phone className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <dt className="sr-only">Phone</dt>
          <dd>
            <Highlight
              text={lead.customer.contactPhone || 'N/A'}
              term={searchTerm}
            />
          </dd>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <dt className="sr-only">Home Dealer</dt>
          <dd className="truncate">
            <Highlight
              text={lead.homeDealer?.name || 'N/A'}
              term={searchTerm}
            />
          </dd>
        </div>
      </dl>

      <dl className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
        <div className="flex gap-1 text-xs text-gray-600">
          <dt>Due Date</dt>
          <dd>
            <TemporalFormatter value={lead.maintenance.dueDate} />
          </dd>
        </div>
      </dl>
    </li>
  );
}
